"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ROLLUP_OPTIONS = exports.MISSING_DIMENSION_VALUES = exports.ENUM_ROLLUP_OPTIONS = exports.DEFAULT_VIZ_LIMIT = void 0;
exports.getChfColumn = getChfColumn;
exports.getDefaultRollupFn = getDefaultRollupFn;
exports.getDimensionDisplayLabel = getDimensionDisplayLabel;
exports.getDimensions = getDimensions;
exports.getGenericAggIndex = getGenericAggIndex;
exports.getHllColumn = getHllColumn;
exports.getIsFastData = getIsFastData;
exports.getMeasurementDimensionLabel = getMeasurementDimensionLabel;
exports.getMeasurementMetricLabel = getMeasurementMetricLabel;
exports.getQueryDuration = getQueryDuration;
exports.getRollupKey = getRollupKey;
exports.getWindowFn = getWindowFn;
exports.hasDimensionValue = hasDimensionValue;
exports.isAggregateQuery = isAggregateQuery;
exports.isDeviceNameDimension = isDeviceNameDimension;
exports.isDirectionalDim = isDirectionalDim;
exports.isDstDim = isDstDim;
exports.isGenericAggMetric = isGenericAggMetric;
exports.isInterfaceIdDimension = isInterfaceIdDimension;
exports.isSrcDim = isSrcDim;
exports.sortResultRows = sortResultRows;
var _startCase2 = _interopRequireDefault(require("lodash/startCase"));
var _orderBy2 = _interopRequireDefault(require("lodash/orderBy"));
var _measurements = require("./measurements");
var _enumUtils = require("./enumUtils");
var _dimensionUtils = require("./dimensionUtils");
var _deviceDimensions = require("./deviceDimensions");
/**
 * Default number of rows whose time series are rendered when `viz.limit` is not set.
 */
const DEFAULT_VIZ_LIMIT = exports.DEFAULT_VIZ_LIMIT = 100;

/**
 * Available standard rollup functions for metric aggregation
 */
const ROLLUP_OPTIONS = exports.ROLLUP_OPTIONS = [{
  value: 'min',
  label: 'Min'
}, {
  value: 'avg',
  label: 'Average'
}, {
  value: 'max',
  label: 'Max'
}, {
  value: 'last',
  label: 'Last'
}, {
  value: 'sum',
  label: 'Total'
}, {
  value: 'p95',
  label: 'p95'
}];

/**
 * A subset of rollup options appropriate for enum values (they return valid values)
 */
const ENUM_ROLLUP_OPTIONS = exports.ENUM_ROLLUP_OPTIONS = [{
  value: 'min',
  label: 'Min'
}, {
  value: 'max',
  label: 'Max'
}, {
  value: 'last',
  label: 'Last'
}];

/**
 * Get the default rollup function for a metric based on its unit type.
 *
 * Different metric types have different semantics for aggregation:
 * - 'health': Use 'min' (lower = worse: 100=critical, 200=warning, 300=healthy)
 * - 'http_status_code': Use 'max' (higher = worse: 5xx > 4xx > 3xx > 2xx)
 * - 'dns_code': Use 'max' (higher codes are generally errors)
 * - default: Use 'avg'
 */
function getDefaultRollupFn(metricInfo) {
  if (metricInfo?.Window === 'count') {
    return 'max';
  }

  // Unit-specific defaults take precedence over the generic enum default below,
  // because these enums are ordered (worst-case aggregation is more useful than
  // the most recent value).
  const unit = metricInfo?.Unit?.split(':')[0];
  switch (unit) {
    case 'health':
      return 'min';
    case 'http_status_code':
    case 'dns_code':
      return 'max';
    case 'timestamp':
      return 'last';
    default:
      break;
  }

  // Unordered enums (e.g. NMS oper/admin status) have no meaningful numeric
  // aggregation, so surface the most recent value.
  if ((0, _enumUtils.isEnumItem)(metricInfo)) {
    return 'last';
  }
  return 'avg';
}
function getWindowFn(metricInfo, rollupFn) {
  if (metricInfo) {
    if (metricInfo.Window) {
      return metricInfo.Window;
    }
    // For SQL-compatible functions (min, max, avg, sum), use directly
    // For post-processing functions (p95, etc.), use 'avg' as SQL base
    const resolvedRollupFn = rollupFn || getDefaultRollupFn(metricInfo);
    if (['avg', 'min', 'max', 'sum'].includes(resolvedRollupFn)) {
      return resolvedRollupFn;
    }
  }
  return 'avg';
}
function getQueryDuration(query) {
  if (query.time.lookback) {
    return query.time.lookback;
  }
  return query.time.end - query.time.start;
}
function getIsFastData(query) {
  const duration = getQueryDuration(query);
  if (!query.window) {
    return false;
  }
  const hasSmallWindowOrDuration = query.window && query?.window.size > 0 && query?.window.size < 3600 || duration <= 60 * 60 * 24;
  if (hasSmallWindowOrDuration) {
    return false;
  }
  if (!query.measurementObj?.storage.SupportsFastData) {
    return false;
  }
  if (query.window && query.window.size >= 3600) {
    return true;
  }
  // doing this now until we properly calculate fast data retention
  return null;
}
function getDimensions(query) {
  return query.dimensions.length === 0 ? [{
    name: 'name'
  }] : query.dimensions;
}
function getRollupKey(rollup) {
  return `${rollup.fn}_${rollup.metric}`;
}

/**
 * Sort result rows by relevance, using the same precedence applied server-side:
 * explicit `query.sort` first, then aggregate metrics, then rollups. Pass
 * `isAggregate` from `isAggregateQuery` so aggregate queries sort by metric.
 */
function sortResultRows(query, rows, {
  isAggregate = false
} = {}) {
  if (query.sort?.length) {
    return (0, _orderBy2.default)(rows, query.sort.map(s => row => row.rollups[s.rollup] ?? 0), query.sort.map(s => s.order ?? 'desc'));
  }
  if (isAggregate) {
    return (0, _orderBy2.default)(rows, query.metrics.map(m => row => row.rollups[m.name] ?? 0), Array(query.metrics.length).fill('desc'));
  }
  const rollups = query.rollups ?? [];
  if (!rollups.length) {
    // No sort and no rollups to rank by — preserve the incoming order.
    return rows;
  }
  return (0, _orderBy2.default)(rows, rollups.map(rollup => {
    const key = getRollupKey(rollup);
    return row => row.rollups[key] ?? 0;
  }), Array(rollups.length).fill('desc'));
}
function isAggregateQuery(query) {
  const {
    measurementObj
  } = query;
  return query.metrics.length > 0 && !!(0, _measurements.getMeasurementMetric)(measurementObj, query.metrics[0].name)?.Aggregate;
}
function isSrcDim(dimensionName) {
  return dimensionName.startsWith('src_') || dimensionName.endsWith('_src') || dimensionName.includes('_src_') || dimensionName.startsWith('input_') || dimensionName.includes('_input_') || dimensionName.endsWith('_in');
}
function isDstDim(dimensionName) {
  return dimensionName.startsWith('dst_') || dimensionName.endsWith('_dst') || dimensionName.includes('_dst_') || dimensionName.startsWith('output_') || dimensionName.includes('_output_') || dimensionName.endsWith('_out');
}
function isDirectionalDim(dimensionName) {
  const hasSrc = isSrcDim(dimensionName);
  const hasDst = isDstDim(dimensionName);
  return hasSrc || hasDst;
}
function getChfColumn(dimensionName) {
  if ((0, _dimensionUtils.isAppProtocolDimension)(dimensionName)) {
    return dimensionName.split('__')[2].toLowerCase();
  }
  return dimensionName;
}

/**
 * Get a display label for a dimension that includes the src/dst prefix when applicable.
 *
 * For dimensions like src_agent_asn or dst_agent_ip, the base Label in the measurement
 * definition is "Agent ASN" or "Agent IP" (without prefix) so the grouped dimension
 * selector UI can show SRC/DST toggle buttons. This function adds the prefix back
 * for contexts where we need to clearly distinguish which direction was selected
 * (e.g., legend headers, sidebar selected dimensions list).
 *
 * @param dimensionName - The dimension name (e.g., 'src_agent_asn', 'dst_agent_ip')
 * @param baseLabel - The base label from the dimension definition (e.g., 'Agent ASN')
 * @returns Display label with prefix if applicable (e.g., 'Src Agent ASN')
 */
function getDimensionDisplayLabel(dimensionName, baseLabel) {
  if ((0, _dimensionUtils.isAppProtocolDimension)(dimensionName)) {
    return baseLabel;
  }
  if (isSrcDim(dimensionName)) {
    return `Src ${baseLabel}`;
  }
  if (isDstDim(dimensionName)) {
    return `Dst ${baseLabel}`;
  }
  return baseLabel;
}
function getMeasurementDimensionLabel(measurement, dimensionName) {
  const dimensionInfo = (0, _measurements.getMeasurementDimension)(measurement, dimensionName);
  const baseLabel = dimensionInfo?.Label || (0, _startCase2.default)(dimensionName);
  return getDimensionDisplayLabel(dimensionName, baseLabel);
}
function getMeasurementMetricLabel(measurement, metricName) {
  const metricInfo = (0, _measurements.getMeasurementMetric)(measurement, metricName);
  const baseLabel = metricInfo?.Label || (0, _startCase2.default)(metricName);
  return metricInfo?.Window === 'count' ? getDimensionDisplayLabel(metricName, baseLabel) : baseLabel;
}

// Matching on the column keeps every app protocol (snmp, st, event_syslog, event_snmp_traps, ...) covered.
// KDE coalesces absent dimensions to '---', and alarm key parts additionally carry blanks and nulls
const MISSING_DIMENSION_VALUES = exports.MISSING_DIMENSION_VALUES = ['', undefined, null, '---'];
function hasDimensionValue(value) {
  return !MISSING_DIMENSION_VALUES.includes(value);
}
function isDeviceNameDimension(dimensionName) {
  return !!dimensionName && getChfColumn(dimensionName) === _deviceDimensions.DEVICE_NAME_DIMENSION;
}
function isInterfaceIdDimension(dimensionName) {
  return !!dimensionName && (_deviceDimensions.INTERFACE_ID_DIMENSIONS.includes(dimensionName) || _deviceDimensions.INTERFACE_ID_DIMENSIONS.includes(getChfColumn(dimensionName)));
}
function getHllColumn(m) {
  if (m?.QueryColumn && !m.QueryColumn.startsWith('i_')) {
    return m.QueryColumn;
  }
  if (m?.Column && !m.Column.startsWith('i_')) {
    return m.Column;
  }
  return null;
}
function isGenericAggMetric(query, metric, measurement) {
  const metricInfo = (0, _measurements.getMeasurementMetric)(measurement, metric.name);
  const column = getHllColumn(metricInfo) || getChfColumn(metric.name);
  const isCount = query.window?.fn?.[metric.name] === 'count';
  if (column === 'agg0') {
    return true;
  }
  if (isCount) {
    const columnLower = column.toLowerCase();
    return !columnLower.startsWith('inet') && !columnLower.startsWith('str');
  }
  return false;
}
function getGenericAggIndex(query, metric, measurement) {
  let aggIndex = 0;
  for (const m of query.metrics) {
    if (m.name === metric.name) {
      return aggIndex;
    }
    if (isGenericAggMetric(query, m, measurement)) {
      aggIndex += 1;
    }
  }
  return aggIndex;
}